import _ from 'lodash'
import LoadingScreen from '@/modules/core/LoadingScreen'
import {ActionTree} from 'vuex'
import {ShippingState} from '@/modules/shipping/store'
import {RootState} from '@/store/types'
import {MutationTypes} from '@/modules/shipping/store/mutations'
import * as api from '@/modules/shipping/api'
import {Colors, notify} from '@/modules/core/notification'
import RateDetailMode from '@/modules/shipping/model/RateDetailMode';
import Carrier from '@/modules/shipping/model/Carrier'
import Rate from '@/modules/shipping/model/Rate'
import Region from '@/modules/shipping/model/Region'
import RegionDetailMode from '@/modules/shipping/model/RegionDetailMode';
import RegionTypes from '@/modules/shipping/model/RegionTypes';
import { Country } from '@/modules/geo/types'

const actions: ActionTree<ShippingState, RootState> = {

    /**
     * Carga en el estado la lista de transportistas registrados en la aplicación
     */
    loadCarrierList: async ({dispatch}) => {
        LoadingScreen.show('Cargando lista de transportistas')
        await dispatch('loadCarriers')

        LoadingScreen.hide()
    },

    /**
     * Acción interna utiliza por loadCarrierList para carga la lista de transportistas
     * si todavía no se ha cargando completamente
     *
     * @internal
     */
    loadCarriers: async ({commit, state}): Promise<Carrier[]> => {
        if (state.carrierListFullyLoaded) {
            return state.carriers
        }

        const carriers = await api.getCarriers()
        commit(MutationTypes.SetCarrierList, carriers)
        commit(MutationTypes.SetCarrierListFullyLoaded, true)

        return carriers
    },

    /**
     * Carga la pantalla del detalle de la tarifa
     */
    loadRateDetail: async ({commit}, param: string) => {
        LoadingScreen.show('Cargando tarifa')

        if (param === 'nueva') {
            commit(MutationTypes.SetCurrentRate, new Rate())
            commit(MutationTypes.SetRateDetailMode, RateDetailMode.Creating)
            LoadingScreen.hide()

            return
        }

        // tslint:disable-next-line:radix
        const id = parseInt(param)
        if (isNaN(id)) {
            notify('El id de la tarifa no es válido', Colors.Error)
            // TODO: Reparar
            // createRouter().push({name: 'shipping.rate-list'})

            return
        }

        const response = await api.getRate(id)

        commit(MutationTypes.SetCurrentRate, response.rate)
        response.regions.forEach((region: Region) => commit(MutationTypes.AddRegion, region))

        commit(MutationTypes.SetRateDetailMode, RateDetailMode.Editing)
        LoadingScreen.hide()
    },

    /**
     * Función que se encarga de modificar el valor de rateDetailMode
     * a través de la mutación SetRateDetailMode.
     *
     * @param value Estado que recibe la acción para editar el estado
     * de la variable rateDetailMode.
     *
     * @author José M. Mármol <jmarmol@softcontrol.es
     */
    editRateDetailMode: ({ commit }, value: RateDetailMode) => {
        if (!value) {
          return
        }
        commit(MutationTypes.SetRateDetailMode, value)
    },

    /**
     * Crea una nueva tarifa en el servidor
     */
    createRate: async ({state, commit, getters}) => {
        const rate = state.currentRate
        const regions = getters.regions(rate)

        // Se crea una constante response con la respuesta obtenida por la api,
        // accedemos a .rate para poder insertar en el estado mediante la mutación
        // la entidad rate en la variable currentRate para poder acceder a su ID
        // desde ella y poder hacer un push cuando la tarifa es creada y no se dupliquen
        // si en el momento de crearla y guardar quieren modificarla.
        const response = await api.createRate(rate, regions)
        commit(MutationTypes.SetCurrentRate, response.rate)
        commit(MutationTypes.UpdateRegions, response.regions)


        notify('La tarifa se ha creado correctamente')
    },

    /**
     * Carga la lista de tarifas en el estado
     */
    loadRateList: async ({commit}) => {
        commit(MutationTypes.SetRateListLoading, true)
        LoadingScreen.show('Cargando lista de tarifas')
        const rates = await api.getRates()
        commit(MutationTypes.SetRateList, rates)

        commit(MutationTypes.SetRateListLoading, false)
        LoadingScreen.hide()
    },

    /**
     * Elimina la tarifa actual en eliminación
     */
    deleteRate: async ({commit, state}) => {
        const rate = state.rateToDelete
        if (!state.rateToDelete) {
            throw new Error('La tarifa a eliminar no tiene ningún id')
        }

        await api.deleteRate(rate)

        // Termino el proceso se notifica al usuario que la tarifa se ha eliminado
        commit(MutationTypes.RemoveRate, rate)
        notify(`La tarifa ${rate} se ha eliminado correctamente`)
        commit(MutationTypes.SetDeletingRate, false)
    },

    /**
     * Genera una nueva tarifa en la inserta en el estado
     */
    buildNewRate: async ({commit}) => {
        commit(MutationTypes.SetCurrentRate, new Rate())
    },

    /**
     * Actualiza la tarifa actualmente en edición
     */
    updateRate: async ({commit, getters, state}) => {
        const rate = state.currentRate

        const data = await api.updateRate(rate, getters.regions(rate))

        commit(MutationTypes.SetCurrentRate, data.rate)
        commit(MutationTypes.ReplaceRegions, {regions: data.regions, rate: data.rate})

        notify(`La tarifa ${data.rate.id} se ha actualizado correctamente`, Colors.Success, {
            subject: 'Envio y transportes',
            goToAction: {
                buttonName: 'Ver tarifa',
                route: {
                    name: 'shipping.rate',
                    params: { id: data.rate.id.toString() },
                },
            },
        })
    },

    /**
     * Resetea las regiones, ya que si salimos de una ficha de tarifa y
     * volvemos a entrar, con la validación de países no permite introducir
     * ninguno más, ya que lo detecta en la lista.
     *
     * @author José M. Mármol <jmarmol@softcontrol.es>
     */
    resetRegions: ({commit}) => {
        commit(MutationTypes.UpdateRegions, [])
    },

    /**
     * Actualiza las regiones existentes por las pasadas por parámetro.
     * Es cierto que esta puede suprimir a la anterior.
     *  --- La cambiaré cuando termine todo lo pendiente ---
     *
     * @author José M. Mármol <jmarmol@softcontrol.es
     */
    updateRegions: ( { commit }, regions: Region[] ) => {
        commit(MutationTypes.UpdateRegions, regions)
    },

    /**
     * Carga un transportista
     *
     * @param commit
     * @param carrier
     */
    loadCarrier ({commit}, carrier: Carrier) {
        commit(MutationTypes.SetCurrentCarrier, _.cloneDeep(carrier))
    },

    /**
     * Crea un nuevo transportista
     */
    buildNewCarrier ({commit}) {
        commit(MutationTypes.SetCurrentCarrier, new Carrier(''))
    },

    /**
     * Guarda los cambios del transportistas actualmente en edición por el usuario
     *
     * @param state
     * @param commit
     */
    async saveCarrier ({state, commit}) {
        let carrier = state.currentCarrier
        carrier = carrier.isNew
            ? await api.createCarrier(carrier)
            : await api.updateCarrier(carrier)

        commit(MutationTypes.SetCurrentCarrier, carrier)
        commit(MutationTypes.AddCarrier, carrier)

        return carrier
    },

    /**
     * Eliminar el transportista actualmente seleccionado
     *
     * @param state
     * @param commit
     */
    async deleteCarrier ({state, commit}) {
        const carrier = state.currentCarrier
        await api.deleteCarrier(carrier.id)

        commit(MutationTypes.DeleteCarrier, carrier.id)
        commit(MutationTypes.SetCurrentCarrier, new Carrier(''))
    },

    /**
     * Añade el transportista seleccionado al estado actual.
     * @param carrier
     */

     updateCurrentCarrier ({ commit }, carrier: Carrier) {
        if (!(carrier instanceof Carrier)) {
            return
        }
        commit(MutationTypes.SetCurrentCarrier, carrier)
     },

    /**
     * Crea una nueva región
     */
    createRegion ({state, commit}) {
        // ES NECESARIO decirle al estado mediante la mutación
        // que la región que se desea introducir es nueva ya que
        // ha entrado en esta función, no tenía sentido
        // importar en una constante la currentRegion, ya que
        // en el componente se cargaban los datos de la anteriormente
        // introducida, los campos los queremos vacíos, vamos a crear 1
        // no editarla.....
        const region = state.currentRegion
        region.rate = state.currentRate
        region.type = RegionTypes.Country
        region.name = ''
        region.geodata = new Country('', '')
        commit(MutationTypes.SetCurrentRegion, region)
        commit(MutationTypes.SetRegionDetailMode, RegionDetailMode.Creating)

    },
}

export default actions
